/**
 * Version control for one artifact (plan 7.5), shared by the web and native
 * panels and lightboxes: the compact control's model, the list rows,
 * moving between versions, and Restore this version. The version on screen
 * lives with the caller (the web panel store, or native local state), so
 * every open starts on the latest version.
 */
import { useCallback, useMemo } from 'react';
import { useArtifactDetail } from '@/hooks/useArtifact';
import { useArtifactVersions, useRestoreArtifactVersion } from '@/hooks/useArtifactVersions';
import { trackArtifactVersionNavigated, type VersionNavigation } from '../artifact-analytics';
import {
  versionControlModel,
  versionRows,
  viewingIdFor,
  type VersionControlModel,
  type VersionRef,
  type VersionRowModel,
} from './version-model';

export type VersionControl = {
  model: VersionControlModel;
  rows: VersionRowModel[];
  loadingRows: boolean;
  rowsFailed: boolean;
  refetchRows: () => void;
  /** Moves to a version; `via` says how, for `artifact_version_navigated`. */
  goTo: (target: VersionRef | null, via: Exclude<VersionNavigation, 'latest'>) => void;
  backToLatest: () => void;
  /** Restores the version on screen; resolves the number it restored, or null on failure. */
  restore: () => Promise<number | null>;
  restoring: boolean;
};

export function useVersionControl(input: {
  artifactId: string | null;
  viewingId: string | null;
  onViewingChange: (versionId: string | null) => void;
  enabled?: boolean;
}): VersionControl {
  const { artifactId, viewingId, onViewingChange } = input;
  const enabled = Boolean(artifactId) && (input.enabled ?? true);
  const detail = useArtifactDetail(artifactId, { enabled });
  const head = detail.data?.currentVersion ?? null;
  const versionCount = detail.data?.versionCount ?? head?.number ?? 0;
  const versions = useArtifactVersions(artifactId, { enabled: enabled && versionCount > 1 });
  const list = useMemo(() => versions.data?.versions ?? [], [versions.data]);
  const model = useMemo(
    () => versionControlModel({ versions: list, viewingId, head, versionCount }),
    [head, list, versionCount, viewingId],
  );
  const rows = useMemo(() => versionRows(list, viewingId), [list, viewingId]);
  const restoreMutation = useRestoreArtifactVersion(artifactId);

  const kind = detail.data?.kind ?? null;
  const goTo = useCallback(
    (target: VersionRef | null, via: Exclude<VersionNavigation, 'latest'>) => {
      if (target) trackArtifactVersionNavigated(via, kind);
      onViewingChange(viewingIdFor(target, head));
    },
    [head, kind, onViewingChange],
  );
  const backToLatest = useCallback(() => {
    trackArtifactVersionNavigated('latest', kind);
    onViewingChange(null);
  }, [kind, onViewingChange]);
  const { refetch } = versions;
  const refetchRows = useCallback(() => {
    void refetch();
  }, [refetch]);

  const restore = useCallback(async () => {
    const current = model.current;
    if (!viewingId || !current || model.isLatest) return null;
    try {
      await restoreMutation.mutateAsync(viewingId);
      onViewingChange(null);
      return current.number;
    } catch {
      return null;
    }
  }, [model, onViewingChange, restoreMutation, viewingId]);

  return {
    model,
    rows,
    loadingRows: versions.isLoading,
    rowsFailed: Boolean(versions.error),
    refetchRows,
    goTo,
    backToLatest,
    restore,
    restoring: restoreMutation.isPending,
  };
}
